import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TextReveal } from "@/components/TextReveal";
import { Instagram, ArrowUpRight } from "lucide-react";
import { INSTAGRAM_POSTS } from "@/data/instagram";
import { SOCIAL } from "@/data/sponsors";
import { api } from "@/lib/api";

export const SocialFeed = () => {
  const [posts, setPosts] = useState(INSTAGRAM_POSTS);

  useEffect(() => {
    api
      .get("/instagram/posts")
      .then(({ data }) => {
        if (Array.isArray(data.posts) && data.posts.length) setPosts(data.posts);
      })
      .catch(() => {});
  }, []);

  return (
  <section
    id="social"
    className="relative bg-[#5E2A52] pb-10 pt-16 sm:pb-12 sm:pt-20 lg:pb-14 lg:pt-24"
    data-testid="social-feed-section"
  >
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E0708A]">
            Dal nostro Instagram
          </p>
          <TextReveal
            text="Gli ultimi post"
            as="h1"
            testId="social-heading"
            className="mt-5 font-display text-4xl tracking-tight text-white sm:text-5xl"
          />
          <p className="mt-4 max-w-lg text-base leading-relaxed text-white/70">
            Gli ultimi contenuti pubblicati sul profilo Instagram di{" "}
            <span className="text-white">DonnaImpresa</span>. Tocca un post per
            aprirlo e commentarlo su Instagram.
          </p>
        </div>
        <a
          href={SOCIAL.instagram}
          target="_blank"
          rel="noreferrer"
          data-testid="social-follow-instagram"
          className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#5E2A52] transition-colors duration-300 hover:bg-[#E0708A] hover:text-white"
        >
          <Instagram size={17} /> Seguici su Instagram
        </a>
      </div>

      <div
        className={`mt-12 grid gap-6 sm:gap-7 ${
          posts.length === 1
            ? "mx-auto max-w-md"
            : posts.length === 2
            ? "mx-auto max-w-4xl sm:grid-cols-2"
            : "sm:grid-cols-2 lg:grid-cols-3"
        }`}
        data-testid="instagram-posts-grid"
      >
        {posts.map((p, i) => (
          <motion.a
            key={p.id}
            href={p.permalink}
            target="_blank"
            rel="noreferrer"
            data-testid={`instagram-post-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="group flex h-full flex-col overflow-hidden rounded-2xl bg-white p-3 transition-transform duration-300 hover:-translate-y-1.5 sm:p-3.5"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl bg-[#F3ECEE] sm:aspect-square">
              <img
                src={p.image}
                alt={p.caption || "Post Instagram di DonnaImpresa"}
                loading="lazy"
                style={{ objectPosition: p.focus || "50% 50%" }}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <span className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#5E2A52] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <ArrowUpRight size={16} />
              </span>
            </div>
            <div className="flex flex-1 flex-col px-3 pb-2 pt-5 sm:px-4">
              <p className="line-clamp-4 text-sm leading-relaxed text-[#1A1617]">
                {p.caption}
              </p>
              <div className="mt-auto flex items-center justify-between gap-3 pt-5 text-xs text-[#6B5E62]">
                <span className="inline-flex items-center gap-1.5">
                  <Instagram size={13} className="text-[#E0708A]" /> DonnaImpresa
                </span>
                <span>{p.data || p.timestamp}</span>
              </div>
            </div>
          </motion.a>
        ))}
      </div>
    </div>
    </section>
  );
};
